import React, { useState, useRef, useEffect } from 'react';
import { SilverstripeGraphQLError, TimeSlot } from '@firsttable/types';
import { useAuthState } from '@context/AuthContext';
import { InitialStateType as AuthStateType } from '@context/AuthContext/reducer';
import SignInModal from '@components/organisms/Modal/SignInModal';
import useBackUrl from '@hooks/backUrl';
import { useModal } from '@context/ModalContext';
import { useAlert } from 'react-alert';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { createRegularBooking as CREATE_BOOKING } from '@firsttable/graphql-queries/src';
import { errorMessage } from '@firsttable/functions';
import useLogError from '@discover-components/booking/page/form/utils/useLogError';
import Logger from '@/lib/logger';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import getRestaurantCartAnalysis from '@/components/analytics/functions/getRestaurantCartAnalysis';

const logger = new Logger('Booking processing');

// Hook
function usePrevious<T>(value: T): T {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T>();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

type Timeslot = {
  time: string;
  guests: number;
  options?: JSON | object;
  timeSlot: TimeSlot;
};

/**
 * The subset of restaurant fields the regular-booking flow actually reads (booking mutation, analytics, sign-in modal). Fields are intentionally optional/nullable so both the legacy `@firsttable/types` `Restaurant` and the restaurant store's `RestaurantToShow` (gql.tada-derived, `| null` shaped) satisfy it. Divergent, unused fields (`lat`/`lng`/`menus`) are deliberately excluded so the two shapes don't need to reconcile them.
 */
export type BookingRestaurant = {
  id?: number | null;
  menuTitle?: string | null;
  city?: string | null;
  cityId?: number | null;
  region?: { id?: number | null; menuTitle?: string | null } | null;
  suburb?: { id?: number | null; menuTitle?: string | null } | null;
  cuisines?: {
    edges?: ({ node?: { title?: string | null } | null } | null)[] | null;
  } | null;
  potentialPoints?: ({ key?: string | null; value?: string | null } | null)[] | null;
  rating?: number | null;
  reviewsCount?: number | null;
  approvedReviewsCount?: number | null;
  showBookingPriceLabel?: boolean | null;
};
export type WithRegularBookingProps = { restaurant: BookingRestaurant };

export type WithRegularBooking = {
  handleBooking: (slot: Timeslot, force?: boolean) => void;
  isProcessing: boolean;
  selectedTime: Timeslot | null;
  setSelectedTime: (slot: Timeslot | null) => void;
};

const withHandleRegularBooking = (WrappedComponent) => {
  const WithHandleRegularBooking = ({ restaurant, ...props }: WithRegularBookingProps) => {
    const { user } = useAuthState();
    const alert = useAlert();
    const router = useRouter();
    const { showModal } = useModal();
    const { setBackUrl } = useBackUrl();
    const [processing, setProcessing] = useState<boolean>(false);
    const [selectedTime, setSelectedTime] = useState<Timeslot | null>(null);
    const lastStep = usePrevious(selectedTime);
    const { track } = useAnalytics();
    const logError = useLogError();
    const [createBooking, { error }] = useMutation(CREATE_BOOKING);

    const handleBooking = (slot: Timeslot, force = false) => {
      logger.log('handle booking', slot);
      setProcessing(true);
      if (!force && !user) {
        setBackUrl(router.asPath);
        showModal(<SignInModal restaurant={restaurant} />, {
          type: 'signInForm',
          hideOnRouteChange: false,
          onRequestClose: (close, state: Partial<AuthStateType> = {}) => {
            // can we re-check user state
            if (state?.user) {
              logger.log('re-try booking', slot);
              handleBooking(slot, true);
              setTimeout(() => close(), 300);
            } else {
              logger.log('clear booking');
              setSelectedTime(lastStep);
              setProcessing(false);
              setBackUrl(null);
              close();
            }
          },
        });
        return;
      }

      const { date, session } = slot.timeSlot;

      createBooking({
        variables: {
          restaurantId: restaurant.id,
          date,
          time: slot.time,
          covers: slot.guests,
          options: JSON.stringify(slot.options),
        },
      })
        .then(async ({ data }) => {
          if (data) {
            track('booking_initialized', {
              restaurant_id: restaurant.id,
              restaurant_name: restaurant.menuTitle,
              restaurant_cuisines: restaurant.cuisines,
              booking_type: 'REGULAR_TABLE',
              booking_selected_date_time: `${date}T${slot.time}`,
              booking_selected_number_of_people: slot.guests,
              city_id: restaurant.cityId,
              city_name: restaurant.city,
              region_id: restaurant?.region?.id,
              region_name: restaurant?.region?.menuTitle,
              price: 0,
              price_nzd: 0,
              rrp_nzd: 0,
              amount_to_pay: 0,
              amount_to_pay_nzd: 0,
              potential_points: +(
                restaurant.potentialPoints?.find((e) => e?.key === 'REGULAR_TABLE')?.value ?? 0
              ),
              booking_reference: data.createRegularBooking?.reference,
              session_selected: session,
              total_reviews: restaurant?.reviewsCount,
              review_rating: restaurant?.rating,
              restaurants: getRestaurantCartAnalysis(restaurant),
            });
            await router.push(`/booking/regular?reference=${data.createRegularBooking.reference}`);
          }
          if (error) {
            logError(
              errorMessage(error),
              { source: 'regular_booking', recoverability: 'recoverable' },
              {
                restaurant_id: restaurant.id,
                restaurant_name: restaurant.menuTitle,
                restaurant_cuisines: restaurant.cuisines,
                booking_type: 'REGULAR_TABLE',
                date_time: `${date}T${slot.time}`,
                number_of_people: slot.guests,
                city_id: restaurant.cityId,
                city_name: restaurant.city,
                region_id: restaurant?.region?.id,
                region_name: restaurant?.region?.menuTitle,
                price: 0,
                price_nzd: 0,
                rrp_nzd: 0,
                amount_to_pay: 0,
                amount_to_pay_nzd: 0,
                potential_points: +(
                  restaurant.potentialPoints?.find((e) => e?.key === 'REGULAR_TABLE')?.value ?? 0
                ),
                booking_reference: data.createRegularBooking?.reference,
                session_selected: session,
              }
            );
            setSelectedTime(null);
            setProcessing(false);
            alert.error(errorMessage(error), {
              timeout: 5000,
              // @ts-expect-error "Object literal may only specify known properties, and 'width' does not exist in type 'AlertCustomOptions'."
              width: [300, 500],
            });
          }
        })
        .catch((e: SilverstripeGraphQLError) => {
          logger.debug('Un-authenticated user', e);
          logError(
            errorMessage(e),
            { source: 'regular_booking', recoverability: 'recoverable' },
            {
              restaurant_id: restaurant.id,
              restaurant_name: restaurant.menuTitle,
              booking_type: 'REGULAR_TABLE',
              date_time: `${date}T${slot.time}`,
              number_of_people: slot.guests,
              city_id: restaurant.cityId,
              city_name: restaurant.city,
              region_id: restaurant?.region?.id,
              region_name: restaurant?.region?.menuTitle,
              session_selected: session,
            }
          );
          // set processing state back to normal to 'unlock' for other bookings
          setProcessing(false);
          setSelectedTime(null);
          alert.error(errorMessage(e), {
            timeout: 5000,
            // @ts-expect-error "Object literal may only specify known properties, and 'width' does not exist in type 'AlertCustomOptions'."
            width: [300, 500],
          });
        });
    };

    const processBooking = (slot?: Timeslot) => {
      track('button_clicked', {
        button_name: 'availability',
        button_copy: `${slot?.timeSlot?.date} ${slot?.timeSlot?.time}`,
        button_type: 'cta',
        table_type: 'REGULAR_TABLE',
      });
      if (slot) {
        setSelectedTime(slot);
      }
      handleBooking(slot ?? selectedTime);
    };

    return (
      <WrappedComponent
        handleBooking={processBooking}
        // processBooking={processBooking}
        isProcessing={processing}
        // bookingStatus={bookingStatus}
        // lastProcessingId={lastProcessingId}
        restaurant={restaurant}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        {...props}
      />
    );
  };
  WithHandleRegularBooking.displayName = 'withHandleRegularBooking';

  return WithHandleRegularBooking;
};

export default withHandleRegularBooking;
